import type { ReactNode } from 'react';
import { cx } from '../cx';

export type ProseProps = {
  /** Already sanitised HTML (job descriptions, CMS articles). Never pass unsanitised input. */
  html?: string;
  children?: ReactNode;
  as?: 'div' | 'article' | 'section';
  /** `sm` for reading text inside a box (a note, a tip, a card): 14px, tighter spacing, full width. */
  size?: 'base' | 'sm';
  className?: string;
};

/**
 * Reading typography for articles, guides and job descriptions: 17/28 text at about 68 characters,
 * display-face headings, links and list markers in the tint colour, one sun-ruled pull quote.
 */
export const proseClass = cx(
  'max-w-xl text-jc-prose text-jc-ink',
  '[&>*+*]:mt-5 [&>:first-child]:mt-0',
  '[&_h2]:mt-10 [&_h2]:type-title',
  '[&_h3]:mt-8 [&_h3]:type-heading',
  '[&_h4]:mt-6 [&_h4]:font-semibold',
  '[&_a]:font-semibold [&_a]:underline [&_a]:decoration-jc-tint [&_a]:decoration-2 [&_a]:underline-offset-2 [&_a:hover]:text-jc-tint',
  '[&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-6 [&_ol]:pl-6 [&_li]:mt-2 [&_li::marker]:text-jc-tint',
  '[&_strong]:font-semibold [&_em]:italic',
  '[&_blockquote]:border-l-4 [&_blockquote]:border-jc-cta [&_blockquote]:pl-5 [&_blockquote]:type-title',
  // A <small> in a quote is its source line: a block cite under the quote, in caption style.
  '[&_blockquote_small]:mt-2 [&_blockquote_small]:block [&_blockquote_small]:type-caption [&_blockquote_small]:text-jc-ink-secondary',
  '[&_code]:rounded-jc-info [&_code]:bg-jc-elevated [&_code]:px-1.5 [&_code]:text-sm [&_code]:text-jc-ink',
  '[&_hr]:border-jc-border [&_img]:rounded-jc-info',
  '[&_table]:w-full [&_table]:text-sm [&_th]:border-b [&_th]:border-jc-border [&_th]:py-2 [&_th]:text-left [&_td]:border-b [&_td]:border-jc-border [&_td]:py-2',
);

const proseSmClass = proseClass.replace('max-w-xl text-jc-prose', 'text-sm').replace('[&>*+*]:mt-5', '[&>*+*]:mt-2');

export function Prose({ html, children, as: Tag = 'div', size = 'base', className }: ProseProps) {
  const base = size === 'sm' ? proseSmClass : proseClass;
  if (html != null) return <Tag className={cx(base, className)} dangerouslySetInnerHTML={{ __html: html }} />;
  return <Tag className={cx(base, className)}>{children}</Tag>;
}
